/**
 * Traffic Agent API
 *
 * Frontend API client for traffic agent link management
 *
 * Date: 2026-03-17
 */

import axios from '@/axios'

export default {
  /**
   * Get pending link confirmations for the current organisation
   * @param {Object} options - Query options
   * @param {number} options.limit - Max results
   * @param {number} options.offset - Pagination offset
   */
  getPendingConfirmations (options = {}) {
    const params = new URLSearchParams()
    if (options.limit) params.append('limit', options.limit)
    if (options.offset) params.append('offset', options.offset)
    return axios.get(`/api/traffic-agent/pending?${params.toString()}`)
      .then(response => response.data)
  },

  /**
   * Confirm a soft-linked TX file
   * @param {number} episodeId - Episode ID to confirm
   * @param {Object} options - Options
   * @param {boolean} options.triggerStaging - Whether to trigger SMB staging
   */
  confirmLink (episodeId, options = {}) {
    return axios.post(`/api/traffic-agent/confirm/${episodeId}`, options)
      .then(response => response.data)
  },

  /**
   * Reject a soft-linked TX file
   * @param {number} episodeId - Episode ID to reject
   * @param {string} reason - Rejection reason
   */
  rejectLink (episodeId, reason = '') {
    return axios.post(`/api/traffic-agent/reject/${episodeId}`, { reason })
      .then(response => response.data)
  },

  /**
   * Create a staging job for an episode
   * @param {number} episodeId - Episode ID to stage
   */
  createStagingJob (episodeId) {
    return axios.post(`/api/traffic-agent/stage/${episodeId}`)
      .then(response => response.data)
  },

  /**
   * Get staging jobs for the current organisation
   * @param {Object} options - Query options
   * @param {string} options.status - Filter by status
   * @param {number} options.limit - Max results
   * @param {number} options.offset - Pagination offset
   */
  getStagingJobs (options = {}) {
    const params = new URLSearchParams()
    if (options.status) params.append('status', options.status)
    if (options.limit) params.append('limit', options.limit)
    if (options.offset) params.append('offset', options.offset)
    return axios.get(`/api/traffic-agent/staging-jobs?${params.toString()}`)
      .then(response => response.data)
  },

  /**
   * Get link status statistics for the current organisation
   */
  getLinkStats () {
    return axios.get(`/api/traffic-agent/stats`)
      .then(response => response.data)
  },

  /**
   * Get episode link details
   * @param {number} episodeId - Episode ID
   */
  getEpisodeLinkDetails (episodeId) {
    return axios.get(`/api/traffic-agent/episode/${episodeId}`)
      .then(response => response.data)
  },

  /**
   * Get presigned URL for TX file preview
   * @param {number} episodeId - Episode ID
   * @param {number} expiresIn - URL expiration time in seconds (default: 3600)
   */
  getTxFilePreviewUrl (episodeId, expiresIn = 3600) {
    return axios.get(`/api/traffic-agent/episode/${episodeId}/preview-url?expiresIn=${expiresIn}`)
      .then(response => response.data)
  }
}
